import { Model } from '@shared/models/Model';
import type { ChipConfig, DataTableHeaders, DotNotationOf } from '@shared/types/Vuetify';
import type { IModel } from '@shared/types/Model';
import { routes, type IndexPayoutEventIncludes, type AllPayoutEventIncludes, type TransactionsInclude } from '@app/purchase/bundle/routes/routes';
import type { ValuesOfConst } from '@shared/types/ApiResponse.ts';
import { usePayoutEventRepository } from '@app/purchase/bundle/repositories/usePayoutEventRepository.ts';
import { useStewardRepository } from '@app/land-hold/steward/repositories/useStewardRepository.ts';

export const PayoutEventStatus = {
    Draft: 'draft',
    Confirmed: 'confirmed',
} as const;

export interface IPayoutEvent extends IModel {
    name: string;
    steward_id: string;
    status: ValuesOfConst<typeof PayoutEventStatus>;
    date: string | null;
    payout_price: number | null;
    currency: string;
    transaction_count: number | null;
    total_payouts: number | null;
    land_owners_count: number | null;
    total_payout_amount: number | null;
    transactions?: TransactionsInclude['transactions'];
}

export class PayoutEvent extends Model implements IPayoutEvent {
    name!: string;
    steward_id!: string;
    status!: ValuesOfConst<typeof PayoutEventStatus>;
    date!: string | null;
    payout_price!: number | null;
    currency!: string;
    transaction_count!: number | null;
    total_payouts!: number | null;
    land_owners_count!: number | null;
    total_payout_amount!: number | null;
    transactions?: TransactionsInclude['transactions'];

    public constructor(o: IPayoutEvent) {
        super(o);
        Object.assign(this, o);
    }

    public static override chipConfig(field: DotNotationOf<AllPayoutEventIncludes>, item: AllPayoutEventIncludes): ChipConfig {
        let color;
        switch (field) {
            case 'status':
                color = PayoutEvent.getStatusColor(item.status);
                return { size: 'small', color, text: item.status };
            default:
                return { size: 'small' };
        }
    }

    public static override chips(): DotNotationOf<AllPayoutEventIncludes>[] {
        return ['status'];
    }

    public static getStatusColor(status: ValuesOfConst<typeof PayoutEventStatus>): string {
        switch (status) {
            case PayoutEventStatus.Draft:
                return 'grey';
            case PayoutEventStatus.Confirmed:
                return 'success';
            default:
                return 'info';
        }
    }

    public static statuses(): string[] {
        return Object.values(PayoutEventStatus);
    }

    public static override empty<T extends IModel>(this: { new: (o: T) => T; create: (o: T) => T }): T {
        return this.create({ currency: 'MXN' } as unknown as T);
    }

    public static override headers(): DataTableHeaders<IndexPayoutEventIncludes> {
        return [
            { title: 'Actions', key: 'actions', sortable: false, fixed: true, width: 113, minWidth: '113', headerProps: { style: { borderRight: '1px solid rgba(0, 0, 0, 0.12)' } } },
            { title: 'Name', key: 'name', link: (item: IndexPayoutEventIncludes) => usePayoutEventRepository().query.show(item.id).endpoint },
            { title: 'Transactions', key: 'transaction_count', sortable: false },
            { title: 'Steward', key: 'steward.data.steward_code', sortable: false, link: (item: IndexPayoutEventIncludes) => useStewardRepository().query.buildGetUrl(item.steward_id).endpoint },
            { title: 'Status', key: 'status' },
            { title: 'Payout price/tCO2e', key: 'payout_price', align: 'center' },
            { title: 'Total Payouts', key: 'total_payouts', sortable: false, align: 'center' },
            { title: 'Date', key: 'date', date: true },
            { title: 'Created At', key: 'created_at' },
            { title: 'Updated At', key: 'updated_at' },
        ];
    }

    public static override routes(): ReturnType<typeof routes> {
        return routes();
    }
}
